import React from 'react';
import { Box, Card, CardMedia, CardContent, Typography } from '@mui/material';
import { MediaImage } from '../../../types/media.types';
import { Badge } from '../../../components/common';

interface ImageCardProps {
  image: MediaImage;
  onClick?: (image: MediaImage) => void;
}

export const ImageCard: React.FC<ImageCardProps> = ({ image, onClick }) => {
  const isFromFacebook = image.source === 'facebook_api';
  const fileSize = formatBytes(image.size || 0);

  // Fix URL to ensure it has /v1/ prefix
  const imageUrl = image.url.includes('/v1/')
    ? image.url
    : image.url.replace('/media/', '/v1/media/');

  return (
    <Card
      onClick={() => onClick?.(image)}
      sx={{
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
      }}
    >
      <CardMedia
        component="img"
        height="150"
        image={imageUrl}
        alt={image.name}
        sx={{
          objectFit: 'cover',
        }}
        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
          e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200"%3E%3Crect fill="%23f3f4f6" width="300" height="200"/%3E%3Ctext x="50%25" y="50%25" font-family="Arial" font-size="14" fill="%239ca3af" text-anchor="middle" dy=".3em"%3EImage Preview%3C/text%3E%3C/svg%3E';
        }}
      />
      <CardContent>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            mb: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={image.name}
        >
          {image.name}
        </Typography>

        <Box sx={{ mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {isFromFacebook ? (
            <>
              <Badge variant="primary" size="small">
                Facebook Ad
              </Badge>
              {image.campaignName && (
                <Badge variant="default" size="small">
                  {image.campaignName}
                </Badge>
              )}
            </>
          ) : (
            <Badge variant="success" size="small">
              Uploaded
            </Badge>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {image.dimensions?.width || 0} × {image.dimensions?.height || 0}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {fileSize}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDate(image.uploadDate)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

function formatDate(dateString: string): string {
  if (!dateString) return 'Unknown';
  return new Date(dateString).toLocaleDateString();
}
